import React, { PropTypes } from 'react';
import { Link } from 'react-router';

import { eventFullPropTypeShape, userProfileShape } from '../../proptypes';
import { stringifyQS } from '../../utils';

import Avatar from '../Avatar';
import Container from '../Container';
import EventLogo from '../EventLogo';
import EventList from '../EventList/EventListContainer.react';
import EventReviewForm from '../../containers/EventReviewForm';
import Masonry from '../Masonry';
import StarRating from '../StarRating';

import { srOnly } from '../../base-styles/mixins.module.css';

import {
  avatar,
  breaker,
  breakerSmall,
  carouselHeading,
  carouselMore,
  container,
  eventDetailContainer,
  eventTitle as eventTitleStyle,
  header,
  ldt,
  link,
  masonry,
  pageHeading,
  eventDetails,
} from './styles.module.css';

const getSimilarQuery = (eventId, races, location) => ({
  resultsPerPage: 6,
  excludeId: eventId,
  sort: 'nearby',
  sport: races && races.races[0].information.sport,
  sportDistance: races && races.races[0].information && races.races[0].information.distance,
  locationLat: location.lat,
  locationLong: location.long,
});

/**
 * Event review presentational component.
 */
const EventReview = ({
  id: eventId,
  images,
  location,
  onFormSuccess,
  organiser,
  profile,
  races,
  response,
  startDate,
  success,
  title: eventTitle,
}) => (
  <div>
    {success && (
      <div style={{ backgroundColor: 'white' }}>
        <header className={header}>
          {response && <StarRating value={response.ratings.overall} />}
          <h1 className={pageHeading}>Thank you, you’re the best!</h1>
          <img
            src="https://d178fu9mi2dmkb.cloudfront.net/uploads/2018/01/25211711/LDT_team_lowres.gif"
            alt="Thank you from everyone at LDT"
            className={ldt}
          />
          <p>
            These races change people’s lives. Your advice will inspire others to find their perfect
            race and we are massively grateful for that.
          </p>
          <h2>Keep up the momentum</h2>
        </header>

        <Container>
          <div>
            <Link
              to={`/s?${stringifyQS(getSimilarQuery(eventId, races, location))}`}
              rel="nofollow"
              className={carouselMore}
            >
              {'See all'}
            </Link>
            <h3 className={carouselHeading}>{'Similar events near to you'}</h3>
            <EventList
              query={getSimilarQuery(eventId, races, location)}
              showLoadMore={false}
              source="review_success"
              carousel
            />
          </div>
        </Container>

        {organiser && (
          <Container>
            <div>
              <Link to={`/o/${organiser.slug}-${organiser.id}`} className={carouselMore}>
                {'See all'}
              </Link>
              <h3 className={carouselHeading}>{`More by ${organiser.title}`}</h3>
              <EventList
                query={{
                  organiser: organiser.id,
                  excludeId: eventId,
                  sort: 'date',
                  resultsPerPage: 6,
                }}
                showLoadMore={false}
                source="review_success"
                carousel
              />
            </div>
          </Container>
        )}
        <Container className={masonry}>
          <Masonry
            collections={[
              'running',
              'road-cycling',
              'triathlon',
              'swimming',
              'obstacle-course',
              'swimrun',
            ]}
          />
        </Container>
      </div>
    )}
    <Container>
      {!success && (
        <div>
          <div className={container}>
            <h1 className={srOnly}>{eventTitle} review</h1>
            {profile && (
              <div>
                <Avatar user={profile} className={avatar} />
                <h2 className={pageHeading}>Hey, {profile.displayName}!</h2>
              </div>
            )}
            <p>Thanks for taking some time to review your race.</p>
          </div>
          <hr className={breakerSmall} />
          <div className={eventDetailContainer}>
            <EventLogo logo={images && images.logo && images.logo.large} small />
            <div className={eventDetails}>
              <div className={eventTitleStyle}>{eventTitle}</div>
              {organiser && (
                <div>
                  <Link to={organiser.permalink} className={link}>
                    By {organiser.title}
                  </Link>
                </div>
              )}
            </div>
          </div>
          <hr className={breaker} />
          <EventReviewForm
            id={eventId}
            onSuccess={onFormSuccess}
            races={races}
            title={eventTitle}
            startDate={startDate}
            profile={profile}
            organiser={organiser}
          />
        </div>
      )}
    </Container>
  </div>
);

EventReview.propTypes = {
  /**
   * Numeric ID of the event.
   */
  id: eventFullPropTypeShape.id, // eslint-disable-line react/require-default-props
  /**
   * Object of images.
   */
  images: eventFullPropTypeShape.images,
  /**
   * Location data object.
   */
  location: eventFullPropTypeShape.location, // eslint-disable-line react/require-default-props
  /**
   * Function called on reveiew form success.
   */
  onFormSuccess: PropTypes.func,
  /**
   * Organiser data object.
   */
  organiser: eventFullPropTypeShape.organiser,
  /**
   * Normalized user profile object.
   */
  profile: userProfileShape,
  /**
   * Object of race data.
   */
  races: eventFullPropTypeShape.races,
  /**
   * Success response object after posting review.
   */
  response: PropTypes.shape({
    /**
     * Object for response ratings.
     */
    ratings: PropTypes.shape({
      /**
       * Numeric aggregate review score.
       */
      overall: PropTypes.number.isRequired,
    }).isRequired,
  }),
  /**
   * ISO string of event start date.
   */
  startDate: eventFullPropTypeShape.startDate, // eslint-disable-line react/require-default-props
  /**
   * Boolean determining whether to show success page.
   */
  success: PropTypes.bool,
  /**
   * String title of the event.
   */
  title: eventFullPropTypeShape.title, // eslint-disable-line react/require-default-props
};

EventReview.defaultProps = {
  images: undefined,
  onFormSuccess: () => {},
  organiser: undefined,
  profile: null,
  races: undefined,
  response: undefined,
  success: false,
};

export default EventReview;



// WEBPACK FOOTER //
// ./app/src/components/EventReview/index.jsx